// Keep this list aligned with the chains `detectNetworkFromAddress` can
// return. A scheme present in a QR but missing from here would survive
// the strip with its `scheme:` intact, then get rejected by the scan
// handler's "unsupported scheme" guard.
const KNOWN_SCHEMES = [
  "bitcoin",
  "ethereum",
  "solana",
  "tron",
  "ton",
  "litecoin",
  "bitcoincash",
  "ripple",
  "zcash",
  "cardano",
  "dogecoin",
  "stellar",
  "near",
] as const

/**
 * Strips a known payment-URI scheme prefix from a string and any trailing
 * `?…` query suffix the address may carry. Case-insensitive on the scheme.
 * Always strips a trailing `?…` even when no scheme prefix was present —
 * some QRs encode bare-address payloads with a query (e.g. `bc1…?amount=`)
 * that would otherwise fail downstream validation.
 *
 * - `bitcoin:bc1abc` → `bc1abc`
 * - `ethereum:0xabc?amount=1` → `0xabc`
 * - `bc1abc?amount=1` → `bc1abc` (bare address with query)
 * - `mailto:foo` → `mailto:foo` (unknown scheme, passthrough)
 * - `0xabc` → `0xabc` (no scheme, passthrough)
 *
 * Why scoped to contact creation rather than `parseScannedRecipient`:
 * existing tests assert the parser passes these schemes through unchanged
 * (parseScannedRecipient.test.ts). Changing that contract would also
 * affect the withdraw scanner — out of scope for this change.
 */
export function stripPaymentUriPrefix(raw: string): string {
  let body = raw

  const colonIndex = body.indexOf(":")
  if (colonIndex > 0) {
    const scheme = body.slice(0, colonIndex).toLowerCase()
    if (KNOWN_SCHEMES.includes(scheme as (typeof KNOWN_SCHEMES)[number])) {
      body = body.slice(colonIndex + 1)
    }
  }

  const queryIndex = body.indexOf("?")
  return queryIndex >= 0 ? body.slice(0, queryIndex) : body
}
